import {
  Alert,
  Box,
  CircularProgress,
  Select,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePatentSearch } from './api';
import { EmptyStateCard } from './components';
import { PATENT_SORT_OPTIONS } from './constants';
import { PatentDocumentsSearch } from './PatentDocumentsSearch';
import { PatentResults } from './PatentResults';
import { PatentSearchForm } from './PatentSearchForm';

import type {
  PatentSearchFilters,
  PatentSearchHydrationState,
  PatentSearchResponse,
} from './types';

interface PatentSearchResultsProps {
  isSearching: boolean;
  generalError: string | null;
  searchQuery: string;
  hasResults: boolean;
  results: PatentSearchResponse | null;
  sortOption: string;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSortChange: (_value: string) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

const PatentSearchResults = ({
  isSearching,
  generalError,
  searchQuery,
  hasResults,
  results,
  sortOption,
  currentPage,
  totalPages,
  totalCount,
  hasNextPage,
  hasPreviousPage,
  onSortChange,
  onNextPage,
  onPreviousPage,
}: PatentSearchResultsProps) => {
  if (generalError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {generalError}
      </Alert>
    );
  }

  if (isSearching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Text variant="body2" color="text.secondary">
            Searching patents...
          </Text>
        </Stack>
      </Box>
    );
  }

  if (hasResults && results) {
    return (
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={{ xs: 1, sm: 0 }}
          sx={{ mb: 2 }}
        >
          <Text variant="body1">
            We Found{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {totalCount.toLocaleString()}
            </Box>{' '}
            patent{totalCount !== 1 ? 's' : ''}
            {searchQuery && (
              <>
                {' '}
                for:{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  &ldquo;{searchQuery}&rdquo;
                </Box>
              </>
            )}
          </Text>
          <Select
            id="patent-search-sort"
            value={sortOption}
            options={PATENT_SORT_OPTIONS.map((opt) => ({
              value: opt.value,
              display: opt.label,
            }))}
            onOptionSelect={onSortChange}
            placeholder="Sort by"
            sx={{
              minWidth: 180,
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { sm: 220 },
            }}
          />
        </Stack>

        <PatentResults
          results={results.patentFileWrapperDataBag}
          currentPage={currentPage}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          isLoading={isSearching}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
        />
      </Box>
    );
  }

  if (searchQuery) {
    return (
      <EmptyStateCard>
        <Text variant="body1" color="text.secondary">
          No patents found for &ldquo;{searchQuery}&rdquo;. Try a different
          search term or adjusting your filters.
        </Text>
      </EmptyStateCard>
    );
  }

  return null;
};

const DEFAULT_SORT_OPTION = 'filingDate_desc';
const DEFAULT_PAGE = 1;
type SearchMode = 'patents' | 'documents';

const EMPTY_FILTERS: PatentSearchFilters = {
  status: '',
  applicationType: '',
  applicant: '',
  inventor: '',
  filingDateFrom: '',
  filingDateTo: '',
  grantDateFrom: '',
  grantDateTo: '',
};

const parsePage = (value: string | null): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return DEFAULT_PAGE;
  }
  return Math.floor(parsed);
};

const parseSortOption = (value: string | null): string => {
  if (!value) {
    return DEFAULT_SORT_OPTION;
  }
  const isKnown = PATENT_SORT_OPTIONS.some((opt) => opt.value === value);
  return isKnown ? value : DEFAULT_SORT_OPTION;
};

const parseSearchMode = (value: string | null): SearchMode => {
  return value === 'documents' ? 'documents' : 'patents';
};

const parseUrlState = (search: string): PatentSearchHydrationState => {
  const params = new URLSearchParams(search);
  return {
    query: (params.get('q') || '').trim(),
    sortOption: parseSortOption(params.get('sort')),
    page: parsePage(params.get('page')),
    filters: {
      status: params.get('status') || '',
      applicationType: params.get('type') || '',
      applicant: params.get('applicant') || '',
      inventor: params.get('inventor') || '',
      filingDateFrom: params.get('filingFrom') || '',
      filingDateTo: params.get('filingTo') || '',
      grantDateFrom: params.get('grantFrom') || '',
      grantDateTo: params.get('grantTo') || '',
    },
  };
};

const setOrDeleteParam = (
  params: URLSearchParams,
  key: string,
  value: string
) => {
  if (value && value.trim().length > 0) {
    params.set(key, value.trim());
    return;
  }
  params.delete(key);
};

export const PatentSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();

  const urlState = useMemo(
    () => parseUrlState(searchParamsString),
    [searchParamsString]
  );
  const searchMode = useMemo(
    () => parseSearchMode(searchParams.get('mode')),
    [searchParams]
  );

  const [inputValue, setInputValue] = useState(urlState.query);
  const [formFilters, setFormFilters] = useState<PatentSearchFilters>(
    urlState.filters
  );

  const [documentsAppNumber, setDocumentsAppNumber] = useState('');
  const [committedDocumentsAppNumber, setCommittedDocumentsAppNumber] =
    useState<string | null>(null);
  const [documentsSearchTrigger, setDocumentsSearchTrigger] = useState(0);
  const [isDocumentsSearching, setIsDocumentsSearching] = useState(false);

  const {
    results,
    isSearching,
    hasResults,
    generalError,
    searchQuery,
    sortOption,
    hydrateFromUrl,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    totalPages,
    totalCount,
  } = usePatentSearch();

  useEffect(() => {
    if (searchMode !== 'patents') {
      return;
    }
    hydrateFromUrl(urlState);
    setInputValue(urlState.query);
    setFormFilters(urlState.filters);
  }, [urlState, hydrateFromUrl, searchMode]);

  const handleSearchModeChange = (nextMode: SearchMode) => {
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      if (nextMode === 'documents') {
        nextParams.set('mode', 'documents');
      } else {
        nextParams.delete('mode');
      }
      return nextParams;
    });
  };

  const commitUrlState = (nextState: PatentSearchHydrationState) => {
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);

      setOrDeleteParam(nextParams, 'q', nextState.query);
      nextParams.set('sort', parseSortOption(nextState.sortOption));

      if (nextState.page > 1) {
        nextParams.set('page', String(nextState.page));
      } else {
        nextParams.delete('page');
      }

      setOrDeleteParam(nextParams, 'status', nextState.filters.status);
      setOrDeleteParam(nextParams, 'type', nextState.filters.applicationType);
      setOrDeleteParam(nextParams, 'applicant', nextState.filters.applicant);
      setOrDeleteParam(nextParams, 'inventor', nextState.filters.inventor);
      setOrDeleteParam(
        nextParams,
        'filingFrom',
        nextState.filters.filingDateFrom
      );
      setOrDeleteParam(nextParams, 'filingTo', nextState.filters.filingDateTo);
      setOrDeleteParam(
        nextParams,
        'grantFrom',
        nextState.filters.grantDateFrom
      );
      setOrDeleteParam(nextParams, 'grantTo', nextState.filters.grantDateTo);

      return nextParams;
    });
  };

  const handlePatentsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    commitUrlState({
      ...urlState,
      query: inputValue.trim(),
      page: DEFAULT_PAGE,
      filters: formFilters,
    });
  };

  const handleDocumentsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = documentsAppNumber.trim();
    if (!normalized) {
      return;
    }
    setCommittedDocumentsAppNumber(normalized);
    setDocumentsSearchTrigger((t) => t + 1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (searchMode === 'documents') {
      handleDocumentsSubmit(e);
    } else {
      handlePatentsSubmit(e);
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = { ...EMPTY_FILTERS };
    setFormFilters(clearedFilters);
    commitUrlState({
      ...urlState,
      page: DEFAULT_PAGE,
      filters: clearedFilters,
    });
  };

  const handleSortChange = (nextSortOption: string) => {
    commitUrlState({
      ...urlState,
      sortOption: nextSortOption,
      page: DEFAULT_PAGE,
    });
  };

  const handleNextPage = () => {
    if (!hasNextPage) {
      return;
    }
    commitUrlState({ ...urlState, page: currentPage + 1 });
  };

  const handlePreviousPage = () => {
    if (!hasPreviousPage) {
      return;
    }
    commitUrlState({
      ...urlState,
      page: Math.max(DEFAULT_PAGE, currentPage - 1),
    });
  };

  return (
    <Box>
      <PatentSearchForm
        inputValue={inputValue}
        onInputChange={setInputValue}
        formFilters={formFilters}
        onFilterChange={(key, value) =>
          setFormFilters((prev) => ({ ...prev, [key]: value }))
        }
        isSearching={isSearching}
        onSubmit={handleSubmit}
        onClearFilters={handleClearFilters}
        searchMode={searchMode}
        onSearchModeChange={handleSearchModeChange}
        documentsAppNumber={documentsAppNumber}
        onDocumentsAppNumberChange={setDocumentsAppNumber}
        isDocumentsSearching={isDocumentsSearching}
      />

      {searchMode === 'documents' && (
        <PatentDocumentsSearch
          applicationNumber={committedDocumentsAppNumber}
          searchTrigger={documentsSearchTrigger}
          onSearchingChange={setIsDocumentsSearching}
        />
      )}

      {searchMode === 'patents' && (
        <PatentSearchResults
          isSearching={isSearching}
          generalError={generalError}
          searchQuery={searchQuery}
          hasResults={hasResults}
          results={results}
          sortOption={sortOption}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onSortChange={handleSortChange}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
        />
      )}
    </Box>
  );
};
